/**
 * Parses stored coveragesAndLimits XML from an ACORD 28 master certificate
 * back into Acord28Form prefill state.
 *
 * XML tag → form state mapping (derived from buildAcord28Xml):
 *   CompletionDate              → certDate
 *   ProducerName                → producerAddr1
 *   ProducerAddressLineOne      → producerAddr2
 *   ProducerAddressLineTwo      → producerAddr3
 *   ProducerCity                → producerCity
 *   ProducerState               → producerState
 *   ProducerZIPCode             → producerZip
 *   Phone / Fax / Email         → producerPhone / producerFax / producerEmail
 *   InsuredName                 → insuredAddr1
 *   AddressLineOne              → insuredAddr2
 *   AddressLineTwo              → insuredAddr3
 *   City / State / ZIPCode      → insuredCity / insuredState / insuredZip
 *   AdditionalInsuredName       → additionalNamedInsureds
 *   LoanNumber / PolicyType     → loanNumber / policyType
 *   ContinuedUntilTerminated    → continuedUntilTerminated
 *   LocationDescription         → locationDescription
 *   PerilsInsured/Basic/Broad/Special → perilsInsured/Basic/Broad/Special
 *   Building / BusinessPersonalProperty → propBuilding / propBusinessPersonalProp
 *   OtherCoverage text attr / content → commPropCovText / commPropCovCheck
 *   CoverageAmount / CoverageDeductible → commPropAmount / commPropDed
 *   BusinessIncome / RentalValue → businessIncome / rentalValue
 *   BusinessLimits text/content → birvLimit / birvYesNoNa
 *   ActualLossSustained text/content → birvMonths / birvActualLoss
 *   Blanket text/content        → blanketAmount / blanketCoverage
 *   Terrorism                   → terrorismCoverage
 *   TerrorismExclusion (first)  → terrorismSpecificExclusion
 *   TerrorismExcluded           → domesticTerrorismExcluded
 *   LimitedFungus text/value/content → limitedFungusLimit/Ded/fungus
 *   FungusExclusion             → fungusExclusion
 *   FungusExclusion_InsurerIdentifier → fungusExclusionForm
 *   FungusExclusion_EditionDate → fungusExclusionDate
 *   FungusExclusion_EntityCopyrightOwnership → fungusExclusionText
 *   ReplacementCost / AgreedValue → replacementCost / agreedValue
 *   Coinsurance text/content    → coinsurancePercent / coinsurance
 *   EquipmentBreakown text/value/content → equipmentBreakdownLimit/Ded/equipmentBreakdown
 *   Loss text/value/content     → ordinanceLimit/Ded/ordinanceOrLaw
 *   Demolition text/value/content → demolitionLimit/Ded/demolitionCostsYN
 *   Construction text/value/content → incrCostLimit/Ded/incrCostYN
 *   EarthMovement text/value/content → earthMovementLimit/Ded/earthMovement
 *   Flood text/value/content    → floodLimit/Ded/flood
 *   Wind text/value/content     → windHailLimit/Ded/windHail
 *   Wind_Yes / Wind_No          → windHailInclYes / windHailInclNo
 *   NamedStorm text/value/yes/no/content → namedStormLimit/Ded/InclYes/InclNo/namedStorm
 *   Permission                  → waiverSubrogation
 *   EvidenceCommercialProperty  → remarks
 *   AdditionalInterestNameAddress → additionalInterestName
 *   LenderServicingAgentNameAddress → lenderServicingAgent
 *
 * policyNumber / effectiveDate / expirationDate come from coverage entry metadata.
 * companyName falls back to policies[n].carrierName when not derivable from XML.
 */

import type { StoredCoverage, StoredPolicy } from './parseAcord25StoredCoverages';
export type { StoredCoverage, StoredPolicy };

type YesNoNa = 'yes' | 'no' | 'na' | '';

export interface Acord28EditPrefill {
  certDate: Date | null;
  producerAddr1: string;
  producerAddr2: string;
  producerAddr3: string;
  producerCity: string;
  producerState: string;
  producerZip: string;
  producerPhone: string;
  producerFax: string;
  producerEmail: string;
  companyName: string;
  naicNo: string;
  policyType: string;
  loanNumber: string;
  policyNumber: string;
  effectiveDate: Date | null;
  expirationDate: Date | null;
  continuedUntilTerminated: boolean;
  replacesPriorDate: Date | null;
  insuredAddr1: string;
  insuredAddr2: string;
  insuredAddr3: string;
  insuredCity: string;
  insuredState: string;
  insuredZip: string;
  additionalNamedInsureds: string;
  locationDescription: string;
  perilsInsured: boolean;
  perilsBasic: boolean;
  perilsBroad: boolean;
  perilsSpecial: boolean;
  propBuilding: boolean;
  propBusinessPersonalProp: boolean;
  commPropCovCheck: boolean;
  commPropCovText: string;
  commPropAmount: string;
  commPropDed: string;
  businessIncome: boolean;
  rentalValue: boolean;
  birvYesNoNa: YesNoNa;
  birvLimit: string;
  birvActualLoss: boolean;
  birvMonths: string;
  blanketCoverage: YesNoNa;
  blanketAmount: string;
  terrorismCoverage: YesNoNa;
  terrorismSpecificExclusion: YesNoNa;
  domesticTerrorismExcluded: YesNoNa;
  limitedFungus: YesNoNa;
  limitedFungusLimit: string;
  limitedFungusDed: string;
  fungusExclusion: YesNoNa;
  fungusExclusionForm: string;
  fungusExclusionDate: Date | null;
  fungusExclusionText: string;
  replacementCost: YesNoNa;
  agreedValue: YesNoNa;
  coinsurance: YesNoNa;
  coinsurancePercent: string;
  equipmentBreakdown: YesNoNa;
  equipmentBreakdownLimit: string;
  equipmentBreakdownDed: string;
  ordinanceOrLaw: YesNoNa;
  ordinanceLimit: string;
  ordinanceDed: string;
  demolitionCostsYN: YesNoNa;
  demolitionLimit: string;
  demolitionDed: string;
  incrCostYN: YesNoNa;
  incrCostLimit: string;
  incrCostDed: string;
  earthMovement: YesNoNa;
  earthMovementLimit: string;
  earthMovementDed: string;
  flood: YesNoNa;
  floodLimit: string;
  floodDed: string;
  windHailInclYes: boolean;
  windHailInclNo: boolean;
  windHail: YesNoNa;
  windHailLimit: string;
  windHailDed: string;
  namedStormInclYes: boolean;
  namedStormInclNo: boolean;
  namedStorm: YesNoNa;
  namedStormLimit: string;
  namedStormDed: string;
  waiverSubrogation: YesNoNa;
  additionalInterestName: string;
  lenderServicingAgent: string;
  authRepSignature: string;
  remarks: string;
  acord101AdditionalRemarks: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseDoc(xml: string): Document {
  return new DOMParser().parseFromString(`<root>${xml}</root>`, 'text/xml');
}

function getText(doc: Document, selector: string): string {
  return doc.querySelector(selector)?.textContent?.trim() || '';
}

function getAttr(doc: Document, selector: string, attr: string): string {
  return doc.querySelector(selector)?.getAttribute(attr)?.trim() || '';
}

function yn(v: string): boolean {
  return ['yes', 'true', '1'].includes(v.trim().toLowerCase());
}

function yesNoNa(v: string): YesNoNa {
  const lv = v.trim().toLowerCase();
  if (lv === 'yes') return 'yes';
  if (lv === 'no') return 'no';
  if (lv === 'na' || lv === 'n/a') return 'na';
  return '';
}

function parseStoredDate(v: string | null | undefined): Date | null {
  if (!v) return null;
  // MM/DD/YYYY or MM/DD/YYYY HH:MM:SS AM/PM
  const slash = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slash) return new Date(Number(slash[3]), Number(slash[1]) - 1, Number(slash[2]));
  const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

// ── Main export ───────────────────────────────────────────────────────────────

export function parseAcord28StoredCoverages(
  coverages: StoredCoverage[],
  policies: StoredPolicy[],
): Acord28EditPrefill {
  const result: Acord28EditPrefill = {
    certDate: null,
    producerAddr1: '', producerAddr2: '', producerAddr3: '',
    producerCity: '', producerState: '', producerZip: '',
    producerPhone: '', producerFax: '', producerEmail: '',
    companyName: '', naicNo: '',
    policyType: '', loanNumber: '', policyNumber: '',
    effectiveDate: null, expirationDate: null,
    continuedUntilTerminated: false, replacesPriorDate: null,
    insuredAddr1: '', insuredAddr2: '', insuredAddr3: '',
    insuredCity: '', insuredState: '', insuredZip: '',
    additionalNamedInsureds: '',
    locationDescription: '',
    perilsInsured: false, perilsBasic: false, perilsBroad: false, perilsSpecial: false,
    propBuilding: false, propBusinessPersonalProp: false,
    commPropCovCheck: false, commPropCovText: '', commPropAmount: '', commPropDed: '',
    businessIncome: false, rentalValue: false,
    birvYesNoNa: '', birvLimit: '', birvActualLoss: false, birvMonths: '',
    blanketCoverage: '', blanketAmount: '',
    terrorismCoverage: '', terrorismSpecificExclusion: '', domesticTerrorismExcluded: '',
    limitedFungus: '', limitedFungusLimit: '', limitedFungusDed: '',
    fungusExclusion: '', fungusExclusionForm: '', fungusExclusionDate: null, fungusExclusionText: '',
    replacementCost: '', agreedValue: '',
    coinsurance: '', coinsurancePercent: '',
    equipmentBreakdown: '', equipmentBreakdownLimit: '', equipmentBreakdownDed: '',
    ordinanceOrLaw: '', ordinanceLimit: '', ordinanceDed: '',
    demolitionCostsYN: '', demolitionLimit: '', demolitionDed: '',
    incrCostYN: '', incrCostLimit: '', incrCostDed: '',
    earthMovement: '', earthMovementLimit: '', earthMovementDed: '',
    flood: '', floodLimit: '', floodDed: '',
    windHailInclYes: false, windHailInclNo: false,
    windHail: '', windHailLimit: '', windHailDed: '',
    namedStormInclYes: false, namedStormInclNo: false,
    namedStorm: '', namedStormLimit: '', namedStormDed: '',
    waiverSubrogation: '',
    additionalInterestName: '', lenderServicingAgent: '',
    authRepSignature: '',
    remarks: '',
    acord101AdditionalRemarks: '',
  };

  // Build policyNumber → carrierName lookup
  const policyCarrierMap: Record<string, string> = {};
  for (const p of policies) {
    if (p.number && p.carrierName) policyCarrierMap[p.number] = p.carrierName;
  }

  for (const cov of coverages) {
    if (cov.signatureId && !result.authRepSignature) result.authRepSignature = cov.signatureId;
    if (!cov.coveragesAndLimits) continue;

    if (cov.acordFormNumber === 101) {
      const doc = parseDoc(cov.coveragesAndLimits);
      result.acord101AdditionalRemarks = getText(doc, 'Acord101AdditionalRemarks RemarksText');
      continue;
    }

    // 28101 = acord28_101 composite form number stored by the server
    if (cov.acordFormNumber !== 28 && cov.acordFormNumber !== 2816 && cov.acordFormNumber !== 28101) {
      // Fallback: catch any entry whose form number was normalized away from 101
      const doc = parseDoc(cov.coveragesAndLimits);
      const remarksText = getText(doc, 'Acord101AdditionalRemarks RemarksText');
      if (remarksText) result.acord101AdditionalRemarks = remarksText;
      continue;
    }

    const doc = parseDoc(cov.coveragesAndLimits);
    if (!doc.querySelector('Acord28AllLiabilities')) {
      // Entry passed form-number check but XML is not a main ACORD 28 block (e.g. a 101 entry
      // whose acordFormNumber was normalized to 28/2816/28101 by the server)
      const remarksText = getText(doc, 'Acord101AdditionalRemarks RemarksText');
      if (remarksText) result.acord101AdditionalRemarks = remarksText;
      continue;
    }

    const R = 'Acord28AllLiabilities';

    // Coverage entry metadata
    result.policyNumber   = cov.policyNumber || '';
    result.effectiveDate  = parseStoredDate(cov.effectiveDate);
    result.expirationDate = parseStoredDate(cov.expirationDate);

    // Producer / agency
    result.certDate      = parseStoredDate(getText(doc, `${R} CompletionDate`));
    result.producerAddr1 = getText(doc, `${R} ProducerName`);
    result.producerAddr2 = getText(doc, `${R} ProducerAddressLineOne`);
    result.producerAddr3 = getText(doc, `${R} ProducerAddressLineTwo`);
    result.producerCity  = getText(doc, `${R} ProducerCity`);
    result.producerState = getText(doc, `${R} ProducerState`);
    result.producerZip   = getText(doc, `${R} ProducerZIPCode`);
    result.producerPhone = getText(doc, `${R} Phone`);
    result.producerFax   = getText(doc, `${R} Fax`);
    result.producerEmail = getText(doc, `${R} Email`);

    // Company — from policy carrier map; naicNo from XML
    result.companyName = policyCarrierMap[cov.policyNumber] || '';
    result.naicNo      = getText(doc, `${R} NaicNo`);

    // Loan / policy
    result.loanNumber  = getText(doc, `${R} LoanNumber`);
    result.policyType  = getText(doc, `${R} PolicyType`);
    result.continuedUntilTerminated = yn(getText(doc, `${R} ContinuedUntilTerminated`));
    result.replacesPriorDate = parseStoredDate(getText(doc, `${R} PriorEvidanceDate`));

    // Insured
    result.insuredAddr1          = getText(doc, `${R} InsuredName`);
    result.insuredAddr2          = getText(doc, `${R} AddressLineOne`);
    result.insuredAddr3          = getText(doc, `${R} AddressLineTwo`);
    result.insuredCity           = getText(doc, `${R} City`);
    result.insuredState          = getText(doc, `${R} State`);
    result.insuredZip            = getText(doc, `${R} ZIPCode`);
    result.additionalNamedInsureds = getText(doc, `${R} AdditionalInsuredName`);
    result.locationDescription   = getText(doc, `${R} LocationDescription`);

    // Perils
    result.perilsInsured = yn(getText(doc, `${R} PerilsInsured`));
    result.perilsBasic   = yn(getText(doc, `${R} Basic`));
    result.perilsBroad   = yn(getText(doc, `${R} Broad`));
    result.perilsSpecial = yn(getText(doc, `${R} Special`));

    // Property
    result.propBuilding            = yn(getText(doc, `${R} Building`));
    result.propBusinessPersonalProp = yn(getText(doc, `${R} BusinessPersonalProperty`));

    // Comm prop coverage
    const otherCovEl = doc.querySelector(`${R} OtherCoverage`);
    result.commPropCovCheck = yn(otherCovEl?.textContent?.trim() || '');
    result.commPropCovText  = otherCovEl?.getAttribute('text') || '';
    result.commPropAmount   = getText(doc, `${R} CoverageAmount`);
    result.commPropDed      = getText(doc, `${R} CoverageDeductible`);

    // Business income / rental value
    result.businessIncome = yn(getText(doc, `${R} BusinessIncome`));
    result.rentalValue    = yn(getText(doc, `${R} RentalValue`));
    const bizLimitsEl = doc.querySelector(`${R} BusinessLimits`);
    result.birvYesNoNa  = yesNoNa(bizLimitsEl?.textContent?.trim() || '');
    result.birvLimit    = bizLimitsEl?.getAttribute('text') || '';
    const actualLossEl  = doc.querySelector(`${R} ActualLossSustained`);
    result.birvActualLoss = yn(actualLossEl?.textContent?.trim() || '');
    result.birvMonths     = actualLossEl?.getAttribute('text') || '';

    // Blanket
    const blanketEl    = doc.querySelector(`${R} Blanket`);
    result.blanketCoverage = yesNoNa(blanketEl?.textContent?.trim() || '');
    result.blanketAmount   = blanketEl?.getAttribute('text') || '';

    // Terrorism
    result.terrorismCoverage         = yesNoNa(getText(doc, `${R} Terrorism`));
    // querySelector gets first TerrorismExclusion (= terrorismSpecificExclusion)
    result.terrorismSpecificExclusion = yesNoNa(getText(doc, `${R} TerrorismExclusion`));
    result.domesticTerrorismExcluded  = yesNoNa(getText(doc, `${R} TerrorismExcluded`));

    // Limited fungus
    const limFungusEl = doc.querySelector(`${R} LimitedFungus`);
    result.limitedFungus      = yesNoNa(limFungusEl?.textContent?.trim() || '');
    result.limitedFungusLimit = limFungusEl?.getAttribute('text') || '';
    result.limitedFungusDed   = limFungusEl?.getAttribute('value') || '';

    // Fungus exclusion
    result.fungusExclusion     = yesNoNa(getText(doc, `${R} FungusExclusion`));
    result.fungusExclusionForm = getText(doc, `${R} FungusExclusion_InsurerIdentifier`);
    result.fungusExclusionDate = parseStoredDate(getText(doc, `${R} FungusExclusion_EditionDate`));
    result.fungusExclusionText = getText(doc, `${R} FungusExclusion_EntityCopyrightOwnership`);

    // Replacement cost / agreed value
    result.replacementCost = yesNoNa(getText(doc, `${R} ReplacementCost`));
    result.agreedValue     = yesNoNa(getText(doc, `${R} AgreedValue`));

    // Coinsurance
    const coinsEl = doc.querySelector(`${R} Coinsurance`);
    result.coinsurance        = yesNoNa(coinsEl?.textContent?.trim() || '');
    result.coinsurancePercent = coinsEl?.getAttribute('text') || '';

    // Equipment breakdown (NowCerts preserves "EquipmentBreakown" typo from our builder)
    const equipEl = doc.querySelector(`${R} EquipmentBreakown`);
    result.equipmentBreakdown      = yesNoNa(equipEl?.textContent?.trim() || '');
    result.equipmentBreakdownLimit = equipEl?.getAttribute('text') || '';
    result.equipmentBreakdownDed   = equipEl?.getAttribute('value') || '';

    // Ordinance or law (<Loss> tag — our builder uses <Loss> for this)
    const lossEl = doc.querySelector(`${R} Loss`);
    result.ordinanceOrLaw = yesNoNa(lossEl?.textContent?.trim() || '');
    result.ordinanceLimit = lossEl?.getAttribute('text') || '';
    result.ordinanceDed   = lossEl?.getAttribute('value') || '';

    // Demolition costs
    const demolEl = doc.querySelector(`${R} Demolition`);
    result.demolitionCostsYN = yesNoNa(demolEl?.textContent?.trim() || '');
    result.demolitionLimit   = demolEl?.getAttribute('text') || '';
    result.demolitionDed     = demolEl?.getAttribute('value') || '';

    // Incr. cost of construction (<Construction> tag)
    const constrEl = doc.querySelector(`${R} Construction`);
    result.incrCostYN    = yesNoNa(constrEl?.textContent?.trim() || '');
    result.incrCostLimit = constrEl?.getAttribute('text') || '';
    result.incrCostDed   = constrEl?.getAttribute('value') || '';

    // Earth movement
    const earthEl = doc.querySelector(`${R} EarthMovement`);
    result.earthMovement      = yesNoNa(earthEl?.textContent?.trim() || '');
    result.earthMovementLimit = earthEl?.getAttribute('text') || '';
    result.earthMovementDed   = earthEl?.getAttribute('value') || '';

    // Flood
    const floodEl = doc.querySelector(`${R} Flood`);
    result.flood      = yesNoNa(floodEl?.textContent?.trim() || '');
    result.floodLimit = floodEl?.getAttribute('text') || '';
    result.floodDed   = floodEl?.getAttribute('value') || '';

    // Wind / hail
    const windEl = doc.querySelector(`${R} Wind`);
    result.windHail      = yesNoNa(windEl?.textContent?.trim() || '');
    result.windHailLimit = windEl?.getAttribute('text') || '';
    result.windHailDed   = windEl?.getAttribute('value') || '';
    result.windHailInclYes = yn(getText(doc, `${R} Wind_Yes`));
    result.windHailInclNo  = yn(getText(doc, `${R} Wind_No`));

    // Named storm
    const stormEl = doc.querySelector(`${R} NamedStorm`);
    result.namedStorm      = yesNoNa(stormEl?.textContent?.trim() || '');
    result.namedStormLimit = stormEl?.getAttribute('text') || '';
    result.namedStormDed   = stormEl?.getAttribute('value') || '';
    result.namedStormInclYes = yn(stormEl?.getAttribute('yes') || '');
    result.namedStormInclNo  = yn(stormEl?.getAttribute('no') || '');

    // Waiver of subrogation
    result.waiverSubrogation = yesNoNa(getText(doc, `${R} Permission`));

    // Remarks (builder stores remarks in <EvidenceCommercialProperty>)
    result.remarks = getText(doc, `${R} EvidenceCommercialProperty`);

    // Additional interest / lender
    result.additionalInterestName = getText(doc, `${R} AdditionalInterestNameAddress`);
    result.lenderServicingAgent   = getText(doc, `${R} LenderServicingAgentNameAddress`);

    // continue processing — may still have an ACORD 101 entry
  }

  return result;
}
